import { BEIGE_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { ConversationMessageFeedback, MessageAction } from 'models/conversations'
import { FC, useState } from 'react'
import { ThumbDown, ThumbDownOutlined, ThumbUp, ThumbUpOutlined } from '@mui/icons-material'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { MessageSuggestions } from '../MessageSuggestions'
import { Nullable } from 'models/helpers'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

interface Props {
  rating?: Nullable<ConversationMessageFeedback>
  retryRating?: Nullable<ConversationMessageFeedback>
  disabled?: boolean
  onRate: (rating: ConversationMessageFeedback, confirmed: boolean) => void
  onChoose?: (rating: ConversationMessageFeedback) => void
  onDismissRetry?: () => void
}

const ICON_SX = { fontSize: '1.6rem' }
const CONFIRM = 'confirm'
const DECLINE = 'decline'

const BUTTONS = [
  { rating: ConversationMessageFeedback.POSITIVE, labelKey: 'chat:thread.answer_feedback.helpful', icon: <ThumbUpOutlined sx={ICON_SX} /> },
  { rating: ConversationMessageFeedback.NEGATIVE, labelKey: 'chat:thread.answer_feedback.not_helpful', icon: <ThumbDownOutlined sx={ICON_SX} /> },
]

const CONFIRMATIONS: Record<ConversationMessageFeedback, { questionKey: string, confirmKey: string, declineKey: string }> = {
  [ConversationMessageFeedback.POSITIVE]: {
    questionKey: 'chat:thread.answer_feedback.positive_question',
    confirmKey: 'chat:thread.answer_feedback.positive_confirm',
    declineKey: 'chat:thread.answer_feedback.positive_decline',
  },
  [ConversationMessageFeedback.NEGATIVE]: {
    questionKey: 'chat:thread.answer_feedback.negative_question',
    confirmKey: 'chat:thread.answer_feedback.negative_confirm',
    declineKey: 'chat:thread.answer_feedback.negative_decline',
  },
}

export const AnswerFeedback: FC<Props> = ({ rating, retryRating, disabled = false, onRate, onChoose, onDismissRetry }) => {
  const { t } = useTranslation(['chat'])
  const [choice, setChoice] = useState<ConversationMessageFeedback | null>(null)

  const confirming = retryRating ?? (!rating ? choice : null)
  const confirmation = confirming ? CONFIRMATIONS[confirming] : null

  const choose = (selected: ConversationMessageFeedback) => {
    setChoice(selected)
    onChoose?.(selected)
  }

  const answer = (action: MessageAction) => {
    if (!confirming) return
    setChoice(null)
    onRate(confirming, action.value === CONFIRM)
  }

  const back = () => {
    setChoice(null)
    if (retryRating) onDismissRetry?.()
  }

  return (
    <Box role="group" aria-label={t('chat:thread.answer_feedback.label')} data-testid="answer-feedback">

      {!!rating && !confirmation &&
        <Box
          role="img"
          aria-label={t(rating === ConversationMessageFeedback.POSITIVE
            ? 'chat:thread.answer_feedback.rated_helpful'
            : 'chat:thread.answer_feedback.rated_not_helpful')}
          sx={{ display: 'flex', padding: '0.4rem', color: GRAY_900 }}
        >
          {rating === ConversationMessageFeedback.POSITIVE ? <ThumbUp sx={ICON_SX} /> : <ThumbDown sx={ICON_SX} />}
        </Box>
      }

      {!rating && !confirmation &&
        <Stack direction="row" alignItems="center" gap="0.2rem">
          {BUTTONS.map((button) => (
            <MUITooltip key={button.rating} content={t(button.labelKey)} placement="bottom">
              <span>
                <IconButton
                  size="small"
                  aria-label={t(button.labelKey)}
                  disabled={disabled}
                  onClick={() => choose(button.rating)}
                  sx={{
                    padding: '0.4rem',
                    color: GRAY_700,
                    '&:hover': { color: GRAY_900, backgroundColor: BEIGE_500 },
                  }}
                >
                  {button.icon}
                </IconButton>
              </span>
            </MUITooltip>
          ))}
        </Stack>
      }

      {!!confirmation &&
        <Stack gap="0.6rem" sx={{ paddingTop: '0.4rem' }}>

          <Typography variant="text-sm" sx={{ color: GRAY_700 }}>
            {t(confirmation.questionKey)}
          </Typography>

          <MessageSuggestions
            actions={[
              { kind: 'reply', value: CONFIRM, label: t(confirmation.confirmKey) },
              { kind: 'reply', value: DECLINE, label: t(confirmation.declineKey) },
            ]}
            locked={disabled}
            onSelect={answer}
          />

          <Box>
            <MUIButton type="secondaryNoBorder" variant="text" size="xs" disabled={disabled} onClick={back}>
              {t('chat:thread.answer_feedback.back')}
            </MUIButton>
          </Box>

        </Stack>
      }

    </Box>
  )
}
